export const COURSE_BASE_HEIGHT = -1.4

export const COURSE_HALF_WIDTH = 1.4
export const WOBBLE_LANE_CENTER_X = 3.6
export const SPECTACLE_LANE_CENTER_X = 7.2
export const COURSE_START_Z = 30
export const COURSE_END_Z = -30
export const COURSE_HOLE_START_Z = 10
export const COURSE_HOLE_END_Z = 0
export const COURSE_HOLE_PLATFORM_DEPTH = 1.1
export const COURSE_HOLE_PERIOD = 1.5
export const COURSE_STEP_TRANSITION_DEPTH = 0.5
export const SPECTACLE_BRIDGE_START_Z = 18
export const SPECTACLE_BRIDGE_END_Z = 8
export const SPECTACLE_BRIDGE_PLATFORM_DEPTH = 1.05
export const SPECTACLE_BRIDGE_PERIOD = 1.8

export const WOBBLE_COURSE_STATIONS = [
  { z: 30, left: 0, right: 0 },
  { z: 28, left: 0, right: 0 },
  { z: 26, left: 0.48, right: -0.02 },
  { z: 24, left: -0.06, right: 0.52 },
  { z: 22, left: 0.55, right: 0 },
  { z: 20, left: -0.04, right: 0.48 },
  { z: 18, left: 0.58, right: -0.06 },
  { z: 16, left: -0.05, right: 0.53 },
  { z: 14, left: 0.5, right: 0.02 },
  { z: 12, left: -0.08, right: 0.56 },
  { z: 10, left: 0.56, right: -0.03 },
  { z: 8, left: -0.05, right: 0.48 },
  { z: 6, left: 0.6, right: -0.08 },
  { z: 4, left: -0.05, right: 0.54 },
  { z: 2, left: 0.52, right: -0.01 },
  { z: 0, left: -0.07, right: 0.58 },
  { z: -2, left: 0.56, right: -0.05 },
  { z: -4, left: -0.03, right: 0.5 },
  { z: -6, left: 0.6, right: -0.07 },
  { z: -8, left: -0.05, right: 0.55 },
  { z: -10, left: 0.53, right: 0 },
  { z: -12, left: -0.08, right: 0.59 },
  { z: -14, left: 0.57, right: -0.05 },
  { z: -16, left: -0.04, right: 0.5 },
  { z: -18, left: 0.59, right: -0.07 },
  { z: -20, left: -0.06, right: 0.55 },
  { z: -22, left: 0.54, right: -0.02 },
  { z: -24, left: -0.04, right: 0.52 },
  { z: -26, left: 0.45, right: 0 },
  { z: -28, left: 0, right: 0 },
  { z: -30, left: 0, right: 0 },
] as const

export const COURSE_OBSTACLE_SPECS = [
  {
    key: 'course-a',
    label: '기본 충돌 장애물',
    massKg: 0.12,
    size: { width: 1.7, height: 0.35, depth: 0.45 },
    position: { x: 0, y: -0.725, z: -26 },
  },
] as const

function transitionHeight(
  z: number,
  centerZ: number,
  positiveZHeight: number,
  negativeZHeight: number,
) {
  const positiveEdge = centerZ + COURSE_STEP_TRANSITION_DEPTH / 2
  const progress = (positiveEdge - z) / COURSE_STEP_TRANSITION_DEPTH
  return positiveZHeight + (negativeZHeight - positiveZHeight) * progress
}

function primaryCourseHeightAt(z: number) {
  // Six former lanes are now connected from +Z to -Z:
  // run-up → steps → slatted holes → low drop → high drop → impact.
  const transitionHalfDepth = COURSE_STEP_TRANSITION_DEPTH / 2
  if (z > 20) return 0
  if (z > 17 + transitionHalfDepth) return 0
  if (z > 17 - transitionHalfDepth) return transitionHeight(z, 17, 0, 0.18)
  if (z > 14 + transitionHalfDepth) return 0.18
  if (z > 14 - transitionHalfDepth) return transitionHeight(z, 14, 0.18, 0.38)
  if (z > 12 + transitionHalfDepth) return 0.38
  if (z > 12 - transitionHalfDepth) return transitionHeight(z, 12, 0.38, 0)
  if (z > COURSE_HOLE_START_Z) return 0
  if (z > COURSE_HOLE_END_Z) {
    const cycle = ((COURSE_HOLE_START_Z - z) % COURSE_HOLE_PERIOD
      + COURSE_HOLE_PERIOD) % COURSE_HOLE_PERIOD
    return cycle <= COURSE_HOLE_PLATFORM_DEPTH ? 0 : COURSE_BASE_HEIGHT
  }
  if (z > -4) return 0
  if (z > -14) return -0.35
  return -0.9
}

function wobbleCourseHeightAt(x: number, z: number) {
  const relativeX = x - WOBBLE_LANE_CENTER_X
  const lateralProgress = (relativeX + COURSE_HALF_WIDTH) / (COURSE_HALF_WIDTH * 2)
  let positiveZStation: (typeof WOBBLE_COURSE_STATIONS)[number]
    = WOBBLE_COURSE_STATIONS[0]
  let negativeZStation: (typeof WOBBLE_COURSE_STATIONS)[number]
    = WOBBLE_COURSE_STATIONS.at(-1)!

  for (let index = 0; index < WOBBLE_COURSE_STATIONS.length - 1; index += 1) {
    const current = WOBBLE_COURSE_STATIONS[index]
    const next = WOBBLE_COURSE_STATIONS[index + 1]
    if (z <= current.z && z >= next.z) {
      positiveZStation = current
      negativeZStation = next
      break
    }
  }

  const longitudinalProgress = (
    positiveZStation.z - z
  ) / (
    positiveZStation.z - negativeZStation.z
  )
  const leftHeight = positiveZStation.left
    + (negativeZStation.left - positiveZStation.left) * longitudinalProgress
  const rightHeight = positiveZStation.right
    + (negativeZStation.right - positiveZStation.right) * longitudinalProgress
  return leftHeight + (rightHeight - leftHeight) * lateralProgress
}

function interpolateHeight(
  z: number,
  positiveZ: number,
  positiveHeight: number,
  negativeZ: number,
  negativeHeight: number,
) {
  const progress = (positiveZ - z) / (positiveZ - negativeZ)
  return positiveHeight + (negativeHeight - positiveHeight) * progress
}

function spectacleCourseHeightAt(z: number) {
  if (z > 24) return 0
  if (z > 20) return interpolateHeight(z, 24, 0, 20, 0.75)
  if (z > SPECTACLE_BRIDGE_START_Z) {
    return interpolateHeight(z, 20, 0.75, SPECTACLE_BRIDGE_START_Z, 0)
  }
  if (z > SPECTACLE_BRIDGE_END_Z) {
    const cycle = ((SPECTACLE_BRIDGE_START_Z - z) % SPECTACLE_BRIDGE_PERIOD
      + SPECTACLE_BRIDGE_PERIOD) % SPECTACLE_BRIDGE_PERIOD
    return cycle <= SPECTACLE_BRIDGE_PLATFORM_DEPTH ? 0 : COURSE_BASE_HEIGHT
  }
  if (z > 5) return interpolateHeight(z, 8, 0, 5, 0.85)
  if (z > 1) return interpolateHeight(z, 5, 0.85, 1, -0.55)
  if (z > -3) return interpolateHeight(z, 1, -0.55, -3, 0.95)
  if (z > -7) return interpolateHeight(z, -3, 0.95, -7, -0.7)
  if (z > -11) return interpolateHeight(z, -7, -0.7, -11, 0.8)
  if (z > -15) return interpolateHeight(z, -11, 0.8, -15, -0.85)
  if (z > -19) return interpolateHeight(z, -15, -0.85, -19, 0.7)
  if (z > -23) return interpolateHeight(z, -19, 0.7, -23, -0.6)
  if (z > -26) return interpolateHeight(z, -23, -0.6, -26, 0.45)
  if (z > -29) return interpolateHeight(z, -26, 0.45, -29, 0)
  return 0
}

export function courseHeightAt(x: number, z: number) {
  if (z < COURSE_END_Z || z > COURSE_START_Z) return COURSE_BASE_HEIGHT
  if (Math.abs(x) <= COURSE_HALF_WIDTH) return primaryCourseHeightAt(z)
  if (Math.abs(x - WOBBLE_LANE_CENTER_X) <= COURSE_HALF_WIDTH) {
    return wobbleCourseHeightAt(x, z)
  }
  if (Math.abs(x - SPECTACLE_LANE_CENTER_X) <= COURSE_HALF_WIDTH) {
    return spectacleCourseHeightAt(z)
  }
  return COURSE_BASE_HEIGHT
}
